import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6ap60tbd17lru29lv80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-20">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-16 shadow-2xl relative overflow-hidden">
          <div className="absolute bottom-0 right-0 text-[120px] font-bold text-white/5 leading-none select-none">
            OPTIMUM
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 relative z-10">
            <div className="lg:col-span-1">
              <h2 className="text-4xl font-bold text-white mb-8">OPTIMUM d.o.o.</h2>
            </div>

            <div className="lg:col-span-1">
              <h3 className="text-lg font-semibold text-white mb-6">Navigation</h3>
              <ul className="space-y-4">
                {['Home', 'About', 'Products', 'Features', 'Contact'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => {
                        const element = document.getElementById(item.toLowerCase());
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer"
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-white mb-6">Get in Touch</h3>
              <div className="space-y-4 text-sm">
                <div>
                  <span className="text-gray-400 font-medium">Address: </span>
                  <span className="text-gray-300">Ulica Vinkovacka Cesta 19a</span>
                </div>
                <div>
                  <span className="text-gray-400 font-medium">City: </span>
                  <span className="text-gray-300">Virovitica</span>
                </div>
                <div>
                  <span className="text-gray-400 font-medium">Zip: </span>
                  <span className="text-gray-300">33000</span>
                </div>
                <div>
                  <span className="text-gray-400 font-medium">Country: </span>
                  <span className="text-gray-300">Croatia</span>
                </div>
                <div>
                  <span className="text-gray-400 font-medium">Email: </span>
                  <a href="mailto:help@optimumdoo.com" className="text-teal-400 hover:text-teal-300 transition-colors cursor-pointer">
                    help@optimumdoo.com
                  </a>
                </div>
                <div>
                  <span className="text-gray-400 font-medium">Phone: </span>
                  <a href="tel:+14474272417" className="text-teal-400 hover:text-teal-300 transition-colors cursor-pointer">
                    +1 4474272417
                  </a>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-lg font-semibold text-white mb-6">Send us a Message</h3>
                <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your Message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors resize-none text-sm"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  {submitStatus === 'success' && (
                    <p className="text-lime-400 text-sm">Message sent successfully!</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-400 text-sm">Failed to send message. Please try again.</p>
                  )}
                </form>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center relative z-10">
            <p className="text-gray-400 text-sm">
              © 2025 OPTIMUM d.o.o. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-400 hover:text-teal-300 transition-colors cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
